// src/components/Dashboard/widgets/SafetyAlertsWidget.tsx
// Widget 7 — Safety Alerts and Incidents
// CRITICAL badge pulses via CSS animation until addressed.
// Click incident card → Incidents module at that record.
// Click overdue CAR → Incidents module at that corrective action.
import { Shield, AlertTriangle, ChevronRight, CheckCircle, Bell } from 'lucide-react'
import { MOCK_WHS_NOTIFICATIONS } from '../../../utils/mockDataWHS'
import { MOCK_CARS } from '../../../utils/mockDataWHSMgmt'
import { MOCK_DASHBOARD } from '../../../utils/mockData'
import { WidgetProps } from '../types'
import { useDeepLinkNavigation } from '../../../hooks/useDeepLinkNavigation'

export function SafetyAlertsWidget({ onNavigate }: WidgetProps) {
  const { navigate } = useDeepLinkNavigation('dashboard', onNavigate)

  const unread      = (MOCK_WHS_NOTIFICATIONS as any[]).filter(
    n => n && (n.acknowledgedCount ?? 0) < (n.deliveredCount ?? 1),
  )
  const overdueCARs = (MOCK_CARS as any[]).filter(
    c => c?.status === 'OPEN' && new Date(c?.dueDate ?? 0) < new Date(),
  )

  // Pull open incident count from MOCK_DASHBOARD metrics
  const openIncidents     = MOCK_DASHBOARD.metrics?.unreadWHSNotifications ?? 0
  const swmsPending       = MOCK_DASHBOARD.hseStatus?.swmsAwaitingApproval ?? 0
  const criticalIncidents = (MOCK_DASHBOARD as any).hseStatus?.criticalIncidents ?? 0

  const totalAlerts = unread.length + overdueCARs.length + criticalIncidents
  const hasCritical = criticalIncidents > 0

  return (
    <div
      className={`border rounded-xl overflow-hidden flex flex-col ${
        hasCritical
          ? 'border-red-300'
          : totalAlerts > 0
          ? 'border-amber-200'
          : 'border-gray-200'
      } bg-white`}
    >
      {/* Header */}
      <div
        className={`px-5 py-3.5 border-b flex items-center justify-between cursor-pointer transition-colors ${
          hasCritical ? 'bg-red-50 hover:bg-red-100 border-red-200' : 'hover:bg-gray-50'
        }`}
        onClick={() => navigate('whs-command')}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Shield className={`w-4 h-4 ${hasCritical ? 'text-red-600' : 'text-orange-600'}`} />
          <span className="font-semibold text-gray-900 text-sm">Safety alerts</span>
          {hasCritical && (
            <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold animate-pulse">
              {criticalIncidents} CRITICAL
            </span>
          )}
          {!hasCritical && totalAlerts > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
              {totalAlerts}
            </span>
          )}
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 divide-x border-b bg-gray-50/60">
        {[
          {
            label: 'Open incidents',
            value: openIncidents,
            cls:   openIncidents > 0 ? 'text-red-600' : 'text-gray-300',
          },
          {
            label: 'SWMS pending',
            value: swmsPending,
            cls:   swmsPending > 0 ? 'text-amber-600' : 'text-gray-300',
          },
          {
            label: 'CARs overdue',
            value: overdueCARs.length,
            cls:   overdueCARs.length > 0 ? 'text-orange-600' : 'text-gray-300',
          },
        ].map(s => (
          <div key={s.label} className="px-3 py-2.5 text-center">
            <div className={`text-xl font-bold ${s.cls}`}>{s.value}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Alert rows */}
      <div className="divide-y divide-gray-50 flex-1">
        {/* Overdue CARs */}
        {overdueCARs.slice(0, 2).map((car: any, idx: number) => (
          <div
            key={car.id ?? idx}
            className="flex items-center gap-3 px-5 py-3 hover:bg-amber-50 cursor-pointer transition-colors group"
            onClick={() => navigate('incidents', { openId: car.id })}
          >
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-700 font-semibold">Corrective action overdue</p>
              <p className="text-sm text-gray-900 truncate">{car.title ?? car.description ?? 'CAR record'}</p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-gray-200 group-hover:text-amber-400 transition-colors" />
          </div>
        ))}

        {/* Unread WHS notifications */}
        {unread.slice(0, 2).map((n: any, idx: number) => (
          <div
            key={n.id ?? idx}
            className="flex items-center gap-3 px-5 py-3 hover:bg-orange-50 cursor-pointer transition-colors group"
            onClick={() => navigate('whs-command')}
          >
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-orange-700 font-semibold">WHS notification unread</p>
              <p className="text-sm text-gray-900 truncate">{n.title ?? n.message ?? 'Safety notification'}</p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-gray-200 group-hover:text-orange-400 transition-colors" />
          </div>
        ))}

        {totalAlerts === 0 && (
          <div className="px-5 py-8 text-center">
            <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-green-700">All clear</p>
            <p className="text-xs text-gray-400">No open safety alerts</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-2.5 border-t flex justify-between text-xs">
        <button onClick={() => navigate('incidents')} className="text-blue-600 hover:underline">
          Incidents →
        </button>
        <button onClick={() => navigate('swms')} className="text-blue-600 hover:underline">
          SWMS →
        </button>
        <button onClick={() => navigate('whs-command')} className="text-blue-600 hover:underline">
          WHS command →
        </button>
      </div>
    </div>
  )
}
